import { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import AppLayout from '@/components/feature/AppLayout';
import { complianceApi, type EInvoiceRecord } from '@/api/compliance.api';
import { useToast } from '@/contexts/ToastContext';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { buildEInvoiceQRText, getEInvoicePublicVerifyUrl } from '@/utils/gstCalculation';
import DashboardTab from './components/DashboardTab';
import BulkRegisterModal from './components/BulkRegisterModal';
import QRCodeViewerModal from './components/QRCodeViewerModal';
import PrintEInvoiceView from './components/PrintEInvoiceView';
import GstinLookupModal from './components/GstinLookupModal';
import IrnLookupModal from './components/IrnLookupModal';

function formatINR(n: number): string {
  return `\u20B9${n.toLocaleString('en-IN')}`;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'GENERATED': return 'bg-emerald-100 text-emerald-700';
    case 'PENDING': return 'bg-amber-100 text-amber-700';
    case 'CANCELLED': return 'bg-gray-100 text-gray-600';
    case 'FAILED': return 'bg-red-100 text-red-600';
    default: return 'bg-gray-100 text-gray-600';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'GENERATED': return 'IRN Generated';
    case 'PENDING': return 'Pending';
    case 'CANCELLED': return 'Cancelled';
    case 'FAILED': return 'Failed — Retry';
    default: return status;
  }
}

const cancelReasons = [
  'Wrong GSTIN',
  'Wrong Invoice Date',
  'Wrong Item Details',
  'Wrong Tax Amount',
  'Duplicate Invoice',
  'Other',
];

export default function EInvoicePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Tab state
  const [activeTab, setActiveTab] = useState<'all' | 'dashboard'>('all');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  // Live E-Invoices from API
  const [eInvoicesList, setEInvoicesList] = useState<EInvoiceRecord[]>([]);

  // Dynamic Sales Invoices from API
  const [dbInvoices, setDbInvoices] = useState<any[]>([]);
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [docDropdownOpen, setDocDropdownOpen] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedDbInvoice, setSelectedDbInvoice] = useState<any | null>(null);
  const [registering, setRegistering] = useState(false);

  const fetchEInvoices = async () => {
    try {
      const res = await complianceApi.getEInvoices({ status: statusFilter, search: searchQuery });
      if (res?.success && Array.isArray(res.data)) {
        setEInvoicesList(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch E-Invoices:', err);
    }
  };

  useEffect(() => {
    fetchEInvoices();
  }, [statusFilter, searchQuery]);

  // Selected E-Invoice
  const [selectedEI, setSelectedEI] = useState<EInvoiceRecord | null>(null);
  const [showDetailSlideOver, setShowDetailSlideOver] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // Register IRN
  const [showRegisterSlideOver, setShowRegisterSlideOver] = useState(false);
  const [regSelectedInvoice, setRegSelectedInvoice] = useState('');
  const [regPreviewInv, setRegPreviewInv] = useState<MockSalesInvoiceDetail | null>(null);

  // Bulk Register
  const [showBulkModal, setShowBulkModal] = useState(false);

  // GSTIN Lookup
  const [showGstinLookup, setShowGstinLookup] = useState(false);

  // IRN Lookup
  const [showIrnLookup, setShowIrnLookup] = useState(false);
  const [irnLookupSeed, setIrnLookupSeed] = useState('');

  // Cancel IRN
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<EInvoiceRecord | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelOtherReason, setCancelOtherReason] = useState('');

  // Retry confirmation
  const [showRetryConfirm, setShowRetryConfirm] = useState(false);
  const [retryTarget, setRetryTarget] = useState<EInvoiceRecord | null>(null);

  // IRP Response collapsible
  const [showIRPResponse, setShowIRPResponse] = useState(false);

  // Auto-trigger from invoice navigation (Register IRN from sales invoice)
  useEffect(() => {
    const state = location.state as { autoRegister?: boolean; invoiceId?: string; invoiceNumber?: string } | null;
    if (state?.autoRegister || state?.invoiceId || state?.invoiceNumber) {
      const targetId = state.invoiceId;
      const targetNumber = state.invoiceNumber;

      setShowRegisterSlideOver(true);
      if (targetNumber) {
        setRegSelectedInvoice(targetNumber);
      }

      (async () => {
        setLoadingInvoices(true);
        try {
          let foundInv: any = null;
          // 1. Fetch exact invoice details if ID is provided
          if (targetId) {
            const detailRes = await complianceApi.getInvoiceById(targetId).catch(() => null);
            if (detailRes?.success && detailRes?.data) {
              foundInv = detailRes.data;
            }
          }

          // 2. If not found by ID, search by invoice number
          if (!foundInv && targetNumber) {
            const searchRes = await complianceApi.searchInvoices(targetNumber).catch(() => null);
            const items = searchRes?.data?.items || searchRes?.data || searchRes || [];
            if (Array.isArray(items) && items.length > 0) {
              foundInv = items.find((i: any) => (i.invoice_number || i.billNo) === targetNumber) || items[0];
            }
          }

          if (foundInv) {
            setSelectedDbInvoice(foundInv);
            setRegSelectedInvoice(foundInv.invoice_number || foundInv.billNo || targetNumber || '');
          }

          // Also populate pending list for the dropdown
          await loadPendingB2BInvoices('');
        } catch (err) {
          console.error('Failed to pre-load invoice data for E-Invoice registration:', err);
        } finally {
          setLoadingInvoices(false);
        }
      })();

      navigate(location.pathname, { replace: true, state: null });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Count B2B invoices without IRN
  const pendingB2B = useMemo(() => {
    return dbInvoices.filter(
      (si) => !eInvoicesList.some((ei) => ei.invoiceId === si.id),
    ).length;
  }, [dbInvoices, eInvoicesList]);

  // Summary
  const summary = useMemo(() => {
    const generated = eInvoicesList.filter((e) => e.status === 'GENERATED').length;
    const cancelled = eInvoicesList.filter((e) => e.status === 'CANCELLED').length;
    const failed = eInvoicesList.filter((e) => e.status === 'FAILED').length;
    return { generated, pending: pendingB2B, cancelled, failed };
  }, [eInvoicesList, pendingB2B]);

  // Filtered records
  const filtered = useMemo(() => {
    let list = [...eInvoicesList];
    if (statusFilter !== 'ALL') {
      list = list.filter((e) => e.status === statusFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (e) =>
          e.invoiceNumber.toLowerCase().includes(q) ||
          e.irn.toLowerCase().includes(q) ||
          e.ackNumber.toLowerCase().includes(q),
      );
    }
    return list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [eInvoicesList, searchQuery, statusFilter]);

  // Handlers
  // Check if can cancel (within 24hrs of creation)
  const canCancelIRN = useCallback((ei: EInvoiceRecord) => {
    if (ei.status !== 'GENERATED') return false;
    const created = new Date(ei.createdAt).getTime();
    return Date.now() - created <= 24 * 60 * 60 * 1000;
  }, []);

  const handleViewDetail = (ei: EInvoiceRecord) => {
    setSelectedEI(ei);
    setShowDetailSlideOver(true);
    setShowIRPResponse(false);
  };

  const handleViewQR = (ei: EInvoiceRecord) => {
    setSelectedEI(ei);
    setShowQRModal(true);
  };

  const handleCancelClick = (ei: EInvoiceRecord) => {
    setCancelTarget(ei);
    setCancelReason('');
    setCancelOtherReason('');
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    const reason = cancelReason === 'Other' ? cancelOtherReason : cancelReason;
    try {
      const res = await complianceApi.cancelEInvoice({ id: cancelTarget.id, cancelReason: reason || 'Not specified' });
      if (res?.success) {
        fetchEInvoices();
      }
    } catch (err) {
      console.error('Failed to cancel E-Invoice:', err);
    } finally {
      setShowCancelModal(false);
      setCancelTarget(null);
    }
  };

  const handleRegisterIRN = async () => {
    if (!selectedDbInvoice && !regSelectedInvoice) return;
    setRegistering(true);
    try {
      const invNo = selectedDbInvoice?.invoice_number || selectedDbInvoice?.invoiceNumber || selectedDbInvoice?.billNo || regSelectedInvoice;
      const invId = selectedDbInvoice?.id;
      const res = await complianceApi.generateEInvoice({ invoiceId: invId, invoiceNumber: invNo });
      if (res?.success) {
        toast.success(res?.message || 'IRN Registered Successfully ✓');
        fetchEInvoices();
        setShowRegisterSlideOver(false);
        setSelectedDbInvoice(null);
        setRegSelectedInvoice('');
      } else {
        toast.error(res?.message || 'Failed to generate IRN');
      }
    } catch (err: any) {
      console.error('Failed to generate E-Invoice:', err);
      const errMsg = err?.response?.data?.message || err?.message || 'Failed to generate E-Invoice';
      toast.error(errMsg);
    } finally {
      setRegistering(false);
    }
  };

  const handleRetryClick = (ei: EInvoiceRecord) => {
    setRetryTarget(ei);
    setShowRetryConfirm(true);
  };

  const handleConfirmRetry = async () => {
    if (!retryTarget) return;
    try {
      const res = await complianceApi.retryEInvoice({ id: retryTarget.id });
      if (res?.success) {
        toast.success('E-Invoice Regenerated Successfully ✓');
        fetchEInvoices();
      } else {
        toast.error(res?.message || 'Failed to retry E-Invoice');
      }
    } catch (err: any) {
      console.error('Failed to retry E-Invoice:', err);
      toast.error(err?.message || 'Failed to retry E-Invoice');
    } finally {
      setShowRetryConfirm(false);
      setRetryTarget(null);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => { });
    toast.info('Copied to clipboard');
  };

  const { selectedWarehouseId, selectedWarehouseName } = useWarehouseStore();

  const loadPendingB2BInvoices = useCallback(async (query: string = '') => {
    setLoadingInvoices(true);
    try {
      // Fetch only B2B transactions (15-digit GSTIN) without IRN, scoped to active warehouse
      const res = await complianceApi.searchInvoices(
        query,
        selectedWarehouseId,
        false,
        true, // pendingEinvoiceOnly
        true, // b2bOnly
      );
      if (res?.success && Array.isArray(res.data?.items)) {
        setDbInvoices(res.data.items);
      } else if (res?.success && Array.isArray(res.data)) {
        setDbInvoices(res.data);
      } else if (Array.isArray(res)) {
        setDbInvoices(res);
      }
    } catch (err) {
      console.error('Failed to load pending B2B invoices:', err);
    } finally {
      setLoadingInvoices(false);
    }
  }, [selectedWarehouseId]);

  const openRegisterSlideOver = async () => {
    setSelectedDbInvoice(null);
    setRegSelectedInvoice('');
    setRegPreviewInv(null);
    setDocSearchQuery('');
    setDocDropdownOpen(false);
    setShowRegisterSlideOver(true);
    await loadPendingB2BInvoices('');
  };

  const filteredInvoices = useMemo(() => {
    // Only B2B invoices (valid 15-char GSTIN) without existing active IRN
    const baseList = dbInvoices.filter((inv: any) => {
      const gstin = (inv.customer_gstin || inv.customerGstin || inv.gstin || '').trim();
      const isB2B = gstin.length === 15;
      const isCancelled = (inv.status || '').toUpperCase() === 'CANCELLED';
      const hasIrnAlready = Boolean(
        inv.hasEinvoice ||
        inv.irn ||
        inv.has_einvoice ||
        eInvoicesList.some(
          (ei) =>
            (ei.invoiceId === inv.id || ei.invoiceNumber === (inv.invoice_number || inv.billNo || inv.invoiceNo)) &&
            ei.status !== 'CANCELLED'
        )
      );
      return isB2B && !isCancelled && !hasIrnAlready;
    });

    if (!docSearchQuery.trim()) return baseList;
    const q = docSearchQuery.toLowerCase().trim();
    return baseList.filter((inv: any) =>
      (inv.invoice_number || inv.billNo || inv.invoiceNo || '').toLowerCase().includes(q) ||
      (inv.customer_name || inv.partyName || '').toLowerCase().includes(q) ||
      (inv.customer_gstin || inv.customerGstin || inv.gstin || '').toLowerCase().includes(q)
    );
  }, [dbInvoices, docSearchQuery, eInvoicesList]);

  return (
    <AppLayout>
      <div className="space-y-6 p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">E-Invoice</h1>
            <p className="text-sm text-gray-500 mt-0.5">Invoice Registration Portal (IRP) compliance</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openRegisterSlideOver}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Register IRN
            </button>
            <button
              onClick={() => setShowBulkModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#4f46e5] bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-stack-line" />
              Bulk Register
            </button>
            <button
              onClick={() => setShowGstinLookup(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-eye-line" />
              GSTIN Lookup
            </button>
            <button
              onClick={() => { setIrnLookupSeed(''); setShowIrnLookup(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-search-line" />
              IRN Lookup
            </button>
            <button
              onClick={() => navigate('/compliance/e-invoice/settings')}
              className="inline-flex items-center gap-1 px-2.5 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              title="Settings"
            >
              <i className="ri-settings-3-line" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            All Records
          </button>
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'dashboard' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Dashboard
          </button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <DashboardTab eInvoices={eInvoicesList} onRetry={handleRetryClick} />
        )}

        {/* All Records Tab */}
        {activeTab === 'all' && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-xs text-gray-500">IRN Generated</span>
                </div>
                <div className="text-2xl font-semibold text-gray-900">{summary.generated}</div>
                <div className="mt-1 text-xs text-gray-500">Successfully registered</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-xs text-gray-500">Pending Registration</span>
                </div>
                <div className="text-2xl font-semibold text-gray-900">{summary.pending}</div>
                <div className="mt-1 text-xs text-gray-500">B2B invoices without IRN</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                  <span className="text-xs text-gray-500">Cancelled</span>
                </div>
                <div className="text-2xl font-semibold text-gray-900">{summary.cancelled}</div>
                <div className="mt-1 text-xs text-gray-500">IRN cancelled</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-xs text-gray-500">Failed</span>
                </div>
                <div className="text-2xl font-semibold text-gray-900">{summary.failed}</div>
                <div className="mt-1 text-xs text-gray-500">Needs action</div>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <div className="relative flex-1 min-w-0">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    placeholder="Search by invoice number, IRN, or ack number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5]"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] cursor-pointer bg-white"
                >
                  <option value="ALL">All Status</option>
                  <option value="GENERATED">IRN Generated</option>
                  <option value="PENDING">Pending</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="FAILED">Failed</option>
                </select>
                <button
                  onClick={() => { setSearchQuery(''); setStatusFilter('ALL'); }}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line" />
                  Reset
                </button>
              </div>
            </div>

            {/* E-Invoice Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-[500px]">
              <div className="px-4 py-3 border-b border-gray-200 shrink-0">
                <h3 className="text-sm font-semibold text-gray-900">E-Invoice Records</h3>
              </div>
              <div className="flex-1 min-h-0 overflow-auto hover-scrollbar bg-white">
                <table className="w-full min-w-[1200px] text-xs">
                  <thead className="sticky top-0 z-20 bg-slate-50">
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2.5 text-left font-medium text-gray-500 whitespace-nowrap">Invoice No</th>
                      <th className="px-3 py-2.5 text-left font-medium text-gray-500 whitespace-nowrap">Customer</th>
                      <th className="px-3 py-2.5 text-left font-medium text-gray-500 whitespace-nowrap">Date</th>
                      <th className="px-3 py-2.5 text-right font-medium text-gray-500 whitespace-nowrap">Value</th>
                      <th className="px-3 py-2.5 text-center font-medium text-gray-500 whitespace-nowrap">IRN Status</th>
                      <th className="px-3 py-2.5 text-left font-medium text-gray-500 whitespace-nowrap">Ack Number</th>
                      <th className="px-3 py-2.5 text-left font-medium text-gray-500 whitespace-nowrap">Ack Date</th>
                      <th className="px-3 py-2.5 text-center font-medium text-gray-500 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-3 py-8 text-center text-gray-400">
                          No E-Invoice records found
                        </td>
                      </tr>
                    ) : (
                      filtered.map((ei) => {
                        return (
                          <tr key={ei.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2.5 font-medium text-[#4f46e5] whitespace-nowrap">{ei.invoiceNumber}</td>
                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{(ei as any).customerName || '—'}</td>
                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{formatDate((ei as any).invoiceDate || ei.ackDate || ei.createdAt)}</td>
                            <td className="px-3 py-2.5 text-right text-gray-600 whitespace-nowrap">{(ei as any).totalAmount ? formatINR((ei as any).totalAmount) : '—'}</td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusBadge(ei.status)}`}>
                                {getStatusLabel(ei.status)}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-gray-600 font-mono whitespace-nowrap">{ei.ackNumber || '—'}</td>
                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{formatDate(ei.ackDate)}</td>
                            <td className="px-3 py-2.5 text-center whitespace-nowrap">
                              <div className="flex items-center justify-center gap-1">
                                {ei.status === 'GENERATED' && (
                                  <>
                                    <button
                                      onClick={() => handleViewDetail(ei)}
                                      className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 cursor-pointer"
                                    >
                                      <i className="ri-eye-line" /> View IRN
                                    </button>
                                    <button
                                      onClick={() => handleViewQR(ei)}
                                      className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-[#4f46e5] bg-indigo-50 rounded hover:bg-indigo-100 cursor-pointer"
                                    >
                                      <i className="ri-qr-code-line" /> View QR
                                    </button>
                                    <button
                                      onClick={() => { setSelectedEI(ei); setShowPrintModal(true); }}
                                      className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-[#6d28d9] bg-purple-50 rounded hover:bg-purple-100 cursor-pointer"
                                    >
                                      <i className="ri-printer-line" /> Print
                                    </button>
                                    {canCancelIRN(ei) && (
                                      <button
                                        onClick={() => handleCancelClick(ei)}
                                        className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 cursor-pointer"
                                      >
                                        <i className="ri-close-circle-line" /> Cancel IRN
                                      </button>
                                    )}
                                  </>
                                )}
                                {ei.status === 'FAILED' && (
                                  <>
                                    <button onClick={() => handleViewDetail(ei)} className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 cursor-pointer">
                                      <i className="ri-eye-line" /> View
                                    </button>
                                    <button onClick={() => handleRetryClick(ei)} className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-[#4f46e5] bg-indigo-50 rounded hover:bg-indigo-100 cursor-pointer">
                                      <i className="ri-refresh-line" /> Retry
                                    </button>
                                  </>
                                )}
                                {ei.status === 'CANCELLED' && (
                                  <>
                                    <button onClick={() => handleViewDetail(ei)} className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 cursor-pointer">
                                      <i className="ri-eye-line" /> View
                                    </button>
                                    <button
                                      onClick={() => { setSelectedEI(ei); setShowPrintModal(true); }}
                                      className="inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-[#6d28d9] bg-purple-50 rounded hover:bg-purple-100 cursor-pointer"
                                    >
                                      <i className="ri-printer-line" /> Print
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ──────────────── IRN Detail Slide-Over ──────────────── */}
        {showDetailSlideOver && selectedEI && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowDetailSlideOver(false)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-lg overflow-y-auto">
              <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">
                  E-Invoice Details — {selectedEI.invoiceNumber}
                </h2>
                <div className="flex items-center gap-2">
                  {selectedEI.irn && (
                    <button
                      onClick={() => { setIrnLookupSeed(selectedEI.irn); setShowIrnLookup(true); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 cursor-pointer"
                    >
                      <i className="ri-shield-check-line" /> Verify with NIC
                    </button>
                  )}
                  <button
                    onClick={() => setShowPrintModal(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#6d28d9] bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 cursor-pointer"
                  >
                    <i className="ri-printer-line" /> Print
                  </button>
                  <button
                    onClick={() => setShowDetailSlideOver(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
                  >
                    <i className="ri-close-line text-lg text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="px-5 py-4 space-y-5">
                {/* Section 1: IRN Information */}
                <section>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">IRN Information</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">IRN</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-900 font-mono text-[10px] font-semibold truncate max-w-[280px]" title={selectedEI.irn}>
                          {selectedEI.irn ? `${selectedEI.irn.slice(0, 14)}...${selectedEI.irn.slice(-10)}` : '—'}
                        </span>
                        {selectedEI.irn && (
                          <button onClick={() => handleCopy(selectedEI.irn)} className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-200 cursor-pointer shrink-0" title="Copy">
                            <i className="ri-file-copy-line text-xs" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Ack Number</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-900 font-mono font-semibold">{selectedEI.ackNumber || '—'}</span>
                        {selectedEI.ackNumber && (
                          <button onClick={() => handleCopy(selectedEI.ackNumber)} className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-200 cursor-pointer" title="Copy">
                            <i className="ri-file-copy-line text-xs" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Ack Date</span>
                      <span className="text-gray-900">{formatDateTime(selectedEI.ackDate)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Status</span>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusBadge(selectedEI.status)}`}>
                        {getStatusLabel(selectedEI.status)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Generated by</span>
                      <span className="text-gray-900">{selectedEI.generatedBy}</span>
                    </div>
                  </div>
                </section>

                {/* Section 2: QR Code */}
                {selectedEI.status === 'GENERATED' && (
                  <section>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">QR Code</h4>
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col items-center">
                      <div className="p-2 bg-white rounded-lg border border-slate-200 shadow-sm">
                        <QRCodeSVG
                          value={getEInvoicePublicVerifyUrl(selectedEI.irn || selectedEI.ackNumber || selectedEI.invoiceNumber)}
                          size={180}
                          level="M"
                        />
                      </div>
                      <a
                        href={getEInvoicePublicVerifyUrl(selectedEI.irn || selectedEI.ackNumber || selectedEI.invoiceNumber)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 mt-2 underline cursor-pointer"
                      >
                        <i className="ri-external-link-line" /> Open Verification URL
                      </a>
                      <p className="text-[10px] text-gray-500 mt-1 font-medium">Scan to view official verified invoice on public portal</p>
                    </div>
                  </section>
                )}

                {/* Section 3: Invoice Details */}
                {selectedEI && (
                  <section>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Invoice Details</h4>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Invoice No</span>
                        <span className="text-gray-900 font-mono font-bold">{selectedEI.invoiceNumber}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Date</span>
                        <span className="text-gray-900">{formatDate(selectedEI.invoiceDate || selectedEI.createdAt)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Customer</span>
                        <span className="text-gray-900 font-medium">{selectedEI.customerName || selectedEI.recipientName || '—'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">GSTIN</span>
                        <span className="text-gray-900 font-mono">{selectedEI.customerGstin || selectedEI.recipientGstin || '—'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Total Value</span>
                        <span className="text-gray-900 font-bold">{formatINR(selectedEI.invoiceTotal || selectedEI.totalAmount)}</span>
                      </div>
                    </div>
                  </section>
                )}

                {/* Section 4: IRP Response (collapsible) */}
                <section>
                  <button onClick={() => setShowIRPResponse(!showIRPResponse)} className="flex items-center gap-2 w-full text-left">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">IRP Response</h4>
                    <i className={`text-gray-400 text-xs transition-transform ${showIRPResponse ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}`} />
                    <span className="text-[10px] text-gray-400">for developers</span>
                  </button>
                  {showIRPResponse && (
                    <div className="mt-2 bg-gray-900 border border-gray-700 rounded-lg p-3">
                      <pre className="text-[11px] text-emerald-400 font-mono whitespace-pre-wrap break-all overflow-x-auto max-h-48 overflow-y-auto">
                        {JSON.stringify(JSON.parse(selectedEI.irpResponse), null, 2)}
                      </pre>
                    </div>
                  )}
                </section>

                {/* Cancel info */}
                {selectedEI.cancelledAt && (
                  <section>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Cancellation</h4>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-red-600">Cancelled At</span>
                        <span className="text-red-700">{formatDateTime(selectedEI.cancelledAt)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-red-600">Reason</span>
                        <span className="text-red-700 font-medium">{selectedEI.cancelReason}</span>
                      </div>
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ──────────────── Register IRN Slide-Over ──────────────── */}
        {showRegisterSlideOver && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowRegisterSlideOver(false)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-lg overflow-y-auto">
              <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Register IRN</h2>
                <button onClick={() => setShowRegisterSlideOver(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                  <i className="ri-close-line text-lg text-gray-500" />
                </button>
              </div>

              <div className="px-5 py-4 space-y-5">
                {/* Step 1: Select Invoice with Searchable Dropdown */}
                <section className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Step 1 — Select Sales Invoice (B2B Only)
                    </h4>
                    {selectedWarehouseName && (
                      <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 flex items-center gap-1">
                        <i className="ri-store-2-line text-xs" />
                        {selectedWarehouseName}
                      </span>
                    )}
                  </div>

                  {selectedWarehouseName && (
                    <div className="mb-3 px-3 py-2 bg-indigo-50/70 border border-indigo-100 rounded-lg flex items-center justify-between text-xs text-indigo-900">
                      <span className="flex items-center gap-1.5 font-medium text-[11px]">
                        <i className="ri-filter-3-line text-indigo-600" />
                        Listing pending B2B invoices for <strong>{selectedWarehouseName}</strong>
                      </span>
                      <span className="text-[10px] font-bold text-indigo-700 bg-white px-2 py-0.5 rounded border border-indigo-200 uppercase tracking-wide">
                        B2B Only
                      </span>
                    </div>
                  )}

                  <div className="relative">
                    <div
                      onClick={() => setDocDropdownOpen(!docDropdownOpen)}
                      className="w-full px-3 py-2.5 text-xs border border-gray-300 rounded-lg bg-white flex items-center justify-between cursor-pointer hover:border-indigo-500 transition-colors shadow-sm"
                    >
                      {selectedDbInvoice ? (
                        <div className="flex items-center gap-2 overflow-hidden">
                          <span className="font-bold text-indigo-700 font-mono bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200 shrink-0">
                            {selectedDbInvoice.invoice_number || selectedDbInvoice.billNo}
                          </span>
                          <span className="text-gray-900 font-medium truncate">
                            {selectedDbInvoice.customer_name || selectedDbInvoice.partyName || 'Customer'}
                          </span>
                          <span className="text-gray-500 text-[11px] shrink-0">
                            ({formatINR(Number(selectedDbInvoice.total_amount || selectedDbInvoice.grandTotal || 0))})
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 font-medium">Search or select B2B Tax Invoice...</span>
                      )}
                      <i className={`ri-arrow-down-s-line text-lg text-gray-400 transition-transform ${docDropdownOpen ? 'rotate-180' : ''}`} />
                    </div>

                    {docDropdownOpen && (
                      <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl z-30 overflow-hidden text-xs max-h-72 flex flex-col">
                        <div className="p-2 border-b border-gray-100 bg-gray-50/80 sticky top-0">
                          <div className="relative">
                            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                            <input
                              type="text"
                              autoFocus
                              value={docSearchQuery}
                              onChange={(e) => setDocSearchQuery(e.target.value)}
                              placeholder="Type invoice number, customer name..."
                              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
                            />
                          </div>
                        </div>

                        <div className="overflow-y-auto divide-y divide-gray-100">
                          {loadingInvoices ? (
                            <div className="p-4 text-center text-gray-400">
                              <i className="ri-loader-4-line animate-spin text-base text-indigo-600 mr-1.5" />
                              Loading Invoices...
                            </div>
                          ) : filteredInvoices.length === 0 ? (
                            <div className="p-4 text-center text-gray-400">No B2B sales invoices found.</div>
                          ) : (
                            filteredInvoices.map((inv: any) => (
                              <div
                                key={inv.id || inv.invoice_number || inv.billNo}
                                onClick={() => {
                                  setSelectedDbInvoice(inv);
                                  setRegSelectedInvoice(inv.invoice_number || inv.billNo);
                                  setDocDropdownOpen(false);
                                }}
                                className={`p-3 hover:bg-indigo-50/60 cursor-pointer transition-colors flex items-center justify-between ${selectedDbInvoice?.id === inv.id || selectedDbInvoice?.billNo === inv.billNo ? 'bg-indigo-50/80' : ''
                                  }`}
                              >
                                <div>
                                  <div className="font-bold text-gray-900 flex items-center gap-2">
                                    <span className="font-mono text-indigo-600">{inv.invoice_number || inv.billNo}</span>
                                    <span className="text-gray-700 font-medium">{inv.customer_name || inv.partyName}</span>
                                  </div>
                                  <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-3">
                                    <span>GSTIN: {inv.customer_gstin || inv.gstin || inv.customerGstin || 'URP'}</span>
                                    <span>Date: {formatDate(inv.invoice_date || inv.date)}</span>
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="font-bold text-gray-900">{formatINR(Number(inv.total_amount || inv.grandTotal || 0))}</div>
                                  {inv.has_einvoice ? (
                                    <span className="text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-1.5 py-0.5 rounded">IRN Active</span>
                                  ) : (
                                    <span className="text-[10px] text-amber-600 font-semibold bg-amber-50 px-1.5 py-0.5 rounded">Ready for IRN</span>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                {/* Step 2: Dynamic Invoice Preview & Review */}
                {selectedDbInvoice && (
                  <section className="space-y-4">
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Step 2 — Review Invoice Payload</h4>
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2.5 text-xs shadow-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Invoice Number</span>
                          <span className="font-bold font-mono text-indigo-600">{selectedDbInvoice.invoice_number || selectedDbInvoice.billNo}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Customer / Recipient</span>
                          <span className="text-gray-900 font-medium">{selectedDbInvoice.customer_name || selectedDbInvoice.partyName}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Recipient GSTIN</span>
                          <span className="text-gray-900 font-mono font-semibold">{selectedDbInvoice.customer_gstin || selectedDbInvoice.gstin || selectedDbInvoice.customerGstin || 'URP'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Invoice Date</span>
                          <span className="text-gray-900">{formatDate(selectedDbInvoice.invoice_date || selectedDbInvoice.date)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Total Amount</span>
                          <span className="text-gray-900 font-bold">{formatINR(Number(selectedDbInvoice.total_amount || selectedDbInvoice.grandTotal || 0))}</span>
                        </div>
                        {selectedDbInvoice.items_count && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Item Count</span>
                            <span className="text-gray-900 font-medium">{selectedDbInvoice.items_count} items</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                )}

                <div className="pt-2 pb-4">
                  <button
                    onClick={handleRegisterIRN}
                    disabled={!selectedDbInvoice || registering}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow-sm transition-colors"
                  >
                    {registering ? (
                      <><i className="ri-loader-4-line animate-spin text-sm" /> Registering with IRP API...</>
                    ) : (
                      <><i className="ri-shield-check-line text-sm" /> Register with Government IRP</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ──────────────── Cancel IRN Modal ──────────────── */}
        {showCancelModal && cancelTarget && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowCancelModal(false)} />
            <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md mx-4 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-2">Cancel IRN for {cancelTarget.invoiceNumber}?</h3>
              <p className="text-sm text-gray-500 mb-4">IRN can only be cancelled within 24 hours.</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Cancel Reason</label>
                  <select value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] cursor-pointer bg-white">
                    <option value="">Select reason...</option>
                    {cancelReasons.map((r) => (<option key={r} value={r}>{r}</option>))}
                  </select>
                </div>
                {cancelReason === 'Other' && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Specify Reason</label>
                    <textarea value={cancelOtherReason} onChange={(e) => setCancelOtherReason(e.target.value)} rows={2} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] resize-none" placeholder="Enter reason..." />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 mt-5">
                <button onClick={() => setShowCancelModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap">Go Back</button>
                <button onClick={handleConfirmCancel} disabled={!cancelReason || (cancelReason === 'Other' && !cancelOtherReason.trim())} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">Confirm Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ──────────────── Retry Confirmation Modal ──────────────── */}
        {showRetryConfirm && retryTarget && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowRetryConfirm(false)} />
            <div className="relative bg-white rounded-lg shadow-lg w-full max-w-sm mx-4 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-2">Retry IRN Registration</h3>
              <p className="text-sm text-gray-500 mb-4">Retry IRN registration for {retryTarget.invoiceNumber}?</p>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setShowRetryConfirm(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={handleConfirmRetry} className="px-4 py-2 text-sm font-medium text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 cursor-pointer whitespace-nowrap">
                  <i className="ri-refresh-line mr-1" /> Retry
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ──────────────── Bulk Register Modal ──────────────── */}
        {showBulkModal && <BulkRegisterModal onClose={() => setShowBulkModal(false)} onSuccess={fetchEInvoices} />}

        {/* ──────────────── GSTIN Lookup Modal ──────────────── */}
        {showGstinLookup && <GstinLookupModal onClose={() => setShowGstinLookup(false)} />}

        {/* ──────────────── IRN Lookup Modal ──────────────── */}
        {showIrnLookup && <IrnLookupModal onClose={() => setShowIrnLookup(false)} initialIrn={irnLookupSeed} />}

        {/* ──────────────── QR Code Viewer Modal ──────────────── */}
        {showQRModal && selectedEI && selectedEI.status === 'GENERATED' && (
          <QRCodeViewerModal ei={selectedEI} onClose={() => setShowQRModal(false)} />
        )}

        {/* ──────────────── Print E-Invoice Modal ──────────────── */}
        {showPrintModal && selectedEI && (
          <PrintEInvoiceView ei={selectedEI} onClose={() => setShowPrintModal(false)} />
        )}
      </div>
    </AppLayout>
  );
}